/* eslint-disable @typescript-eslint/prefer-regexp-exec */
import { Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { AmplitudeService } from '@inyova/inyova-shared';
import { Customer } from '@inyova/models';
import { TranslateService } from '@ngx-translate/core';

import { BehaviorSubject } from 'rxjs';

export interface LanguageListItem {
  code: Languages;
  fullName: string;
  shortName: string;
}
export interface LanguageList {
  [index: number]: LanguageListItem;
}
export enum Languages {
  DE_CH = 'de-CH',
  DE_DE = 'de-DE',
  EN_CH = 'en-CH',
  EN_DE = 'en-DE',
  FR_CH = 'fr-CH'
}
export const languagesCH: LanguageList = [
  { code: Languages.DE_CH, fullName: 'Deutsch', shortName: 'DE' },
  { code: Languages.EN_CH, fullName: 'English', shortName: 'EN' },
  { code: Languages.FR_CH, fullName: 'Français', shortName: 'FR' }
];
export const languagesDE: LanguageList = [
  { code: Languages.DE_DE, fullName: 'Deutsch', shortName: 'DE' },
  { code: Languages.EN_DE, fullName: 'English', shortName: 'EN' }
];

export const SELECTED_LANGUAGE = 'selectedLanguage';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  customer: Customer;
  selected = new BehaviorSubject<Languages>(Languages.DE_CH);

  constructor(
    private translateService: TranslateService,
    private metaService: Meta,
    private titleService: Title,
    private amplitudeService: AmplitudeService
  ) {}

  setInitialAppLanguage(): void {
    // When language was already selected use this one
    const selectedLanguage = localStorage.getItem(SELECTED_LANGUAGE) as Languages;

    if (
      window.location.href.includes('profil-erstellen') ||
      window.location.href.includes('creer-profil') ||
      window.location.href.includes('create-profile')
    ) {
      this.translateService.setDefaultLang(Languages.DE_CH);
      this.setBrowserLanguage();
    } else {
      if (selectedLanguage) {
        this.translateService.setDefaultLang(selectedLanguage);
        this.setLanguage(selectedLanguage);
      } else {
        this.translateService.setDefaultLang(Languages.DE_CH);
        this.setBrowserLanguage();
      }
    }
  }

  setLanguage(lang: Languages): void {
    this.setLanguageAndMetaData(lang);
  }

  getListOfLanguages(customerAppLocation?: Customer['app_location']): LanguageList {
    const domainParts = window.location.host.split('.');
    const appLocation = customerAppLocation || domainParts[domainParts.length - 1];

    let selectedLanguages: LanguageList;

    switch (appLocation) {
      case 'de':
        selectedLanguages = languagesDE;
        break;
      default:
        selectedLanguages = languagesCH;
        break;
    }

    return selectedLanguages;
  }

  getRedirectLanguage(): string {
    return this.translateService.currentLang.slice(0, 2);
  }

  // If there is browser language avaliabile set it as initial one
  private setBrowserLanguage(): void {
    const browserLang = this.translateService.getBrowserLang();

    if (window.location.href.includes('profil-erstellen')) {
      if (window.location.href.includes('.de')) {
        return this.setLanguage(Languages.DE_DE);
      }
      return this.setLanguage(Languages.DE_CH);
    }

    if (window.location.href.includes('creer-profil')) {
      if (window.location.href.includes('.de')) {
        return this.setLanguage(Languages.DE_DE);
      }
      return this.setLanguage(Languages.FR_CH);
    }

    if (window.location.href.includes('.de')) {
      if (browserLang && browserLang.match(/^en/)) {
        return this.setLanguage(Languages.EN_DE);
      }
      return this.setLanguage(Languages.DE_DE);
    }

    if (browserLang && browserLang.match(/^en/)) {
      return this.setLanguage(Languages.EN_CH);
    }
    if (browserLang && browserLang.match(/^fr/)) {
      return this.setLanguage(Languages.FR_CH);
    }

    return this.setLanguage(Languages.DE_CH);
  }

  // Backward compatibility because of old language codes
  private setLanguageAndMetaData(lang: string): void {
    let selectedLanguage = Languages.DE_CH;
    switch (lang) {
      case 'en':
      case 'en-CH':
        selectedLanguage = Languages.EN_CH;
        this.setMetaDataEN();
        break;
      case 'en-DE':
        selectedLanguage = Languages.EN_DE;
        this.setMetaDataEN();
        break;
      case 'fr':
      case 'fr-CH':
        selectedLanguage = Languages.FR_CH;
        this.setMetaDataFR();
        break;
      case 'de-DE':
        selectedLanguage = Languages.DE_DE;
        this.setMetaDataDE();
        break;
      default:
        selectedLanguage = Languages.DE_CH;
        this.setMetaDataDE();
        break;
    }
    this.translateService.use(selectedLanguage);
    this.selected.next(selectedLanguage);
    document.documentElement.lang = selectedLanguage;
    localStorage.setItem(SELECTED_LANGUAGE, selectedLanguage);
    this.amplitudeService.setDisplayedLanguage(selectedLanguage);
  }

  private setMetaDataDE(provider = 'Inyova'): void {
    this.titleService.setTitle(`${provider} | Investiere in Deine Werte`);
    this.metaService.updateTag({
      name: 'description',
      content: `${provider} ist die führende Plattform für Impact Investing. Nachhaltig, transparent, ohne Einschränkungen beim Ertrag.`
    });
  }

  private setMetaDataEN(provider = 'Inyova'): void {
    this.titleService.setTitle(`${provider} | Invest in Your Values`);
    this.metaService.updateTag({
      name: 'description',
      content: `${provider} is the leading platform for impact investing. Sustainable, transparent, with market-rate returns.`
    });
  }

  private setMetaDataFR(): void {
    this.titleService.setTitle('Inyova | Investis dans Tes Valeurs');
    this.metaService.updateTag({
      name: 'description',
      content: 'Inyova est la principale plateforme d’investissement d’impact. Durable, transparente et sans compromis sur la rentabilité.'
    });
  }
}
